/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { type FailedResponse } from './types'
import { ApiUtil } from './api-utils'
import {
  mapAdminDeviceForProvisionCsvImportResponse,
  mapAdminDeviceForProvisionResponseItem,
  mapAdminDeviceProfilesResponse,
  mapAdminDevicesForProvisionResponse,
  mapAdminTenantsResponse
} from './response-mappers'
import {
  type AdminDeviceProfileResponseItem,
  type AdminDeviceProvisionsResponseItem,
  type AdminTenantResponseItem
} from './admin-device-provisions-api-types'

/* API related constants */

const ADMIN_DEVICE_PROVISIONS_API_PATH = '/admin/deviceProvisions'
const ADMIN_DEVICE_PROFILES_API_PATH = `${ADMIN_DEVICE_PROVISIONS_API_PATH}/profiles`
const ADMIN_TENANTS_API_PATH = `${ADMIN_DEVICE_PROVISIONS_API_PATH}/tenants`

/* Response types */

interface GetDeviceProfilesSuccessResponse {
  succeeded: true
  payload: {
    profiles: AdminDeviceProfileResponseItem[]
  }
}

export type GetDeviceProfilesResponse = GetDeviceProfilesSuccessResponse | FailedResponse

interface GetTenantsSuccessResponse {
  succeeded: true
  payload: {
    tenants: AdminTenantResponseItem[]
  }
}

export type GetTenantsResponse = GetTenantsSuccessResponse | FailedResponse

interface GetDevicesForProvisionSuccessResponse {
  succeeded: true
  payload: {
    devices: AdminDeviceProvisionsResponseItem[]
  }
}

export type GetDevicesForProvisionResponse = GetDevicesForProvisionSuccessResponse | FailedResponse

/**
 * Success response for 'create', 'delete' and 'update' Device profile API endpoint responses.
 */
interface UpdateDeviceForProvisionSuccessResponse {
  succeeded: true
  payload: {
    device: AdminDeviceProvisionsResponseItem
  }
}

/**
 * Response for 'create', 'delete' and 'update' Device profile API endpoint responses.
 */
export type UpdateDeviceForProvisionResponse = UpdateDeviceForProvisionSuccessResponse | FailedResponse

interface UploadDeviceForProvisionCsvSuccessResponse {
  succeeded: true
  payload: {
    devices: AdminDeviceProvisionsResponseItem[]
    error: string | null
  }
}

/**
 * Response for uploading device for profiles CSV.
 */
export type UploadDeviceForProvisionCsvResponse = UploadDeviceForProvisionCsvSuccessResponse | FailedResponse

/* Request input types */

export interface DeviceForProvisionCreateRequestPayload {
  devEUI: string
  serialNumber: string
  appEUI: string
  appKey: string
  deviceProfileId: string
  tenantName: string
}

export interface DeviceForProvisionUpdateRequestPayload {
  devEUI: string
  serialNumber: string
  appEUI: string
  appKey: string
  deviceProfileId: string
}

/* Export(s) */

/**
 * Backend API for admin device provisions.
 */
export const AdminDeviceProvisionsAPI = {
  createDeviceForProvision,
  deleteDeviceForProvision,
  getDevicesForProvision,
  getDevicesForProvisionByTenantAndStartingWithSerial,
  getDeviceProfiles,
  getTenants,
  updateDeviceForProvision,
  uploadDeviceForProvisionCsv
}

/* API methods */

/**
 * Create new device for provisions.
 * @param input payload data.
 * @param token Bearer token.
 */
async function createDeviceForProvision(
  input: DeviceForProvisionCreateRequestPayload,
  token: string
): Promise<UpdateDeviceForProvisionResponse> {
  const apiDesc = 'create device for provision request'
  const url = ApiUtil.getApiUrl(ADMIN_DEVICE_PROVISIONS_API_PATH)
  const body = { ...input }

  try {
    const response = await ApiUtil.doPostRequest(url, body, token)
    if (!response.ok) {
      return await ApiUtil.errorResponse(response, apiDesc)
    }
    const payload: unknown = await response.json()
    const parsedPayload = mapAdminDeviceForProvisionResponseItem(payload)
    return {
      succeeded: true,
      payload: {
        device: parsedPayload
      }
    }
  } catch (e) {
    return ApiUtil.unknownExceptionResponse(e, url, apiDesc)
  }
}

/**
 * Delete existing device for provisions.
 * @param id ID of the device for provision to be deleted.
 * @param token Bearer token.
 */
async function deleteDeviceForProvision(
  id: string,
  token: string
): Promise<UpdateDeviceForProvisionResponse> {
  const apiDesc = 'delete device for provision request'
  const url = ApiUtil.getApiUrl(`${ADMIN_DEVICE_PROVISIONS_API_PATH}/${id}`)

  try {
    const response = await ApiUtil.doDeleteRequest(url, token)
    if (!response.ok) {
      return await ApiUtil.errorResponse(response, apiDesc)
    }
    const payload: unknown = await response.json()
    const parsedPayload = mapAdminDeviceForProvisionResponseItem(payload)
    return {
      succeeded: true,
      payload: {
        device: parsedPayload
      }
    }
  } catch (e) {
    return ApiUtil.unknownExceptionResponse(e, url, apiDesc)
  }
}

/**
 * Get all existing devices for provision.
 *
 * @param token Bearer token.
 * @param tenantName Tenant's name to add as query parameter to filter the devices for provision by tenant.
 * Fetches all devices for provision if tenantName is not provided regardless of the tenant.
 */
async function getDevicesForProvision(token: string, tenantName: string | null): Promise<GetDevicesForProvisionResponse> {
  const apiDesc = 'get devices for provision request'
  const queryParams = tenantName ? `?tenantName=${tenantName}` : ''
  const url = ApiUtil.getApiUrl(`${ADMIN_DEVICE_PROVISIONS_API_PATH}${queryParams}`)

  try {
    const response = await ApiUtil.doGetRequest(url, token)
    if (!response.ok) {
      return await ApiUtil.errorResponse(response, apiDesc)
    }
    const payload: unknown = await response.json()
    const parsedPayload = mapAdminDevicesForProvisionResponse(payload)
    return {
      succeeded: true,
      payload: {
        devices: parsedPayload
      }
    }
  } catch (e) {
    return ApiUtil.unknownExceptionResponse(e, url, apiDesc)
  }
}

/**
 * Get devices for provision matching tenant and serial number of sequence after the serial number.
 *
 * @param tenantName Tenant's name to filter the devices for provision by tenant.
 * @param startSerial Serial number to start the search from.
 * @param token Bearer token.
 */
async function getDevicesForProvisionByTenantAndStartingWithSerial(tenantName: string, startSerial: string, token: string): Promise<GetDevicesForProvisionResponse> {
  const apiDesc = "get a tenant's devices for provision starting with serialNo request"
  const url = ApiUtil.getApiUrl(`${ADMIN_DEVICE_PROVISIONS_API_PATH}/listFromSerial/${tenantName}/${startSerial}`)

  try {
    const response = await ApiUtil.doGetRequest(url, token)
    if (!response.ok) {
      return await ApiUtil.errorResponse(response, apiDesc)
    }
    const payload: unknown = await response.json()
    const parsedPayload = mapAdminDevicesForProvisionResponse(payload)
    return {
      succeeded: true,
      payload: {
        devices: parsedPayload
      }
    }
  } catch (e) {
    return ApiUtil.unknownExceptionResponse(e, url, apiDesc)
  }
}

/**
 * Get available device profiles.
 * @param token Bearer token.
 */
async function getDeviceProfiles(token: string): Promise<GetDeviceProfilesResponse> {
  const apiDesc = 'get device profiles request'
  const url = ApiUtil.getApiUrl(ADMIN_DEVICE_PROFILES_API_PATH)

  try {
    const response = await ApiUtil.doGetRequest(url, token)
    if (!response.ok) {
      return await ApiUtil.errorResponse(response, apiDesc)
    }
    const payload: unknown = await response.json()
    const parsedPayload = mapAdminDeviceProfilesResponse(payload)
    return {
      succeeded: true,
      payload: {
        profiles: parsedPayload
      }
    }
  } catch (e) {
    return ApiUtil.unknownExceptionResponse(e, url, apiDesc)
  }
}

/**
 * Get available tenants.
 *
 * @param token Bearer token.
 */
async function getTenants(token: string): Promise<GetTenantsResponse> {
  const apiDesc = 'get tenants request'
  const url = ApiUtil.getApiUrl(ADMIN_TENANTS_API_PATH)

  try {
    const response = await ApiUtil.doGetRequest(url, token)
    if (!response.ok) {
      return await ApiUtil.errorResponse(response, apiDesc)
    }
    const payload: unknown = await response.json()
    const parsedPayload = mapAdminTenantsResponse(payload)
    return {
      succeeded: true,
      payload: {
        tenants: parsedPayload
      }
    }
  } catch (e) {
    return ApiUtil.unknownExceptionResponse(e, url, apiDesc)
  }
}

/**
 * Update existing device for provisions.
 * @param id Device for provision ID.
 * @param input payload data to replace the existing data with.
 * @param token Bearer token.
 */
async function updateDeviceForProvision(
  id: string,
  input: DeviceForProvisionUpdateRequestPayload,
  token: string
): Promise<UpdateDeviceForProvisionResponse> {
  const apiDesc = 'update device for provision request'
  const url = ApiUtil.getApiUrl(`${ADMIN_DEVICE_PROVISIONS_API_PATH}/${id}`)
  const body = { ...input }

  try {
    const response = await ApiUtil.doPutRequest(url, body, token)
    if (!response.ok) {
      return await ApiUtil.errorResponse(response, apiDesc)
    }
    const payload: unknown = await response.json()
    const parsedPayload = mapAdminDeviceForProvisionResponseItem(payload)
    return {
      succeeded: true,
      payload: {
        device: parsedPayload
      }
    }
  } catch (e) {
    return ApiUtil.unknownExceptionResponse(e, url, apiDesc)
  }
}

/**
 * Upload device for provision CSV file (to be imported).
 *
 * @param input Payload form data.
 * @param token Bearer token.
 */
async function uploadDeviceForProvisionCsv(
  input: FormData,
  token: string
): Promise<UploadDeviceForProvisionCsvResponse> {
  const apiDesc = 'upload devices for provision CSV request'
  const url = ApiUtil.getApiUrl(`${ADMIN_DEVICE_PROVISIONS_API_PATH}/import`)

  try {
    const response = await ApiUtil.doPostRequest(url, input, token)
    if (!response.ok) {
      return await ApiUtil.errorResponse(response, apiDesc)
    }
    const payload: unknown = await response.json()
    const parsedPayload = mapAdminDeviceForProvisionCsvImportResponse(payload)

    return {
      succeeded: true,
      payload: {
        devices: parsedPayload.importedDevices ?? [],
        error: parsedPayload.error
      }
    }
  } catch (e) {
    return ApiUtil.unknownExceptionResponse(e, url, apiDesc)
  }
}
